import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material"
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"
import { AuditEventRow } from "@/models/auditEvent"
import { humanizeField } from "@/lib/humanize"
import { enumLabel } from "@/lib/enumLabels"
import type { TFunction } from "i18next"

// Detects "YYYY-MM-DD" (date-only) and full ISO 8601 timestamps. Anything
// else is treated as a regular value.
const ISO_DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/
const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/

type Props = {
    event: AuditEventRow
}

function formatValue(
    t: TFunction,
    model: string,
    field: string,
    value: unknown,
    emptyLabel: string,
): string {
    if (value === null || value === undefined || value === "") return emptyLabel

    const enumed = enumLabel(t, model, field, value)
    if (enumed !== null) return enumed

    if (typeof value === "boolean") return value ? "true" : "false"
    if (typeof value === "number") {
        if (Number.isInteger(value)) return String(value)
        // Round to at most 2 decimals; strip trailing zeros so 1.5 stays "1.5".
        return parseFloat(value.toFixed(2)).toString()
    }
    if (typeof value === "string") {
        if (ISO_DATE_ONLY_RE.test(value)) return dayjs(value).format("DD-MM-YYYY")
        if (ISO_DATETIME_RE.test(value)) return dayjs(value).format("DD-MM-YYYY HH:mm")
        return value
    }
    if (typeof value === "object") return JSON.stringify(value)
    return String(value)
}

function isEmpty(value: unknown): boolean {
    return value === null || value === undefined || value === ""
}

export default function EventDiff({ event }: Props) {
    const { t } = useTranslation()
    const empty = t("history.diff.empty")
    const entries = Object.entries(event.object_changes)

    if (entries.length === 0) {
        return <Typography variant="body2" color="text.secondary">{empty}</Typography>
    }

    const cellWrapSx = {
        wordBreak: "break-word" as const,
        overflowWrap: "anywhere" as const,
        px: { xs: 1, sm: 2 },
    }

    return (
        <TableContainer
            sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
            }}
        >
            <Table size="small" sx={{ tableLayout: "fixed" }}>
                <TableHead>
                    <TableRow sx={{ bgcolor: "action.hover" }}>
                        <TableCell sx={{ ...cellWrapSx, fontWeight: 600, width: "30%" }}>
                            {t("history.diff.field")}
                        </TableCell>
                        <TableCell sx={{ ...cellWrapSx, fontWeight: 600, width: "35%" }}>
                            {t("history.diff.old")}
                        </TableCell>
                        <TableCell sx={{ ...cellWrapSx, fontWeight: 600, width: "35%" }}>
                            {t("history.diff.new")}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {entries.map(([field, [oldValue, newValue]], idx) => {
                        const oldEmpty = isEmpty(oldValue)
                        const newEmpty = isEmpty(newValue)
                        const zebra = idx % 2 === 1

                        return (
                            <TableRow
                                key={field}
                                sx={{
                                    bgcolor: zebra ? "action.hover" : "transparent",
                                    "&:last-child td": { borderBottom: 0 },
                                }}
                            >
                                <TableCell sx={{ ...cellWrapSx, fontWeight: 600 }}>
                                    {humanizeField(t, event.auditable_type, field)}
                                </TableCell>
                                <TableCell
                                    sx={{ ...cellWrapSx, color: oldEmpty ? "text.disabled" : "error.main" }}
                                >
                                    {formatValue(t, event.auditable_type, field, oldValue, empty)}
                                </TableCell>
                                <TableCell
                                    sx={{ ...cellWrapSx, color: newEmpty ? "text.disabled" : "success.main" }}
                                >
                                    {formatValue(t, event.auditable_type, field, newValue, empty)}
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    )
}
